import React, { useState, useEffect } from "react";
import { FileText, AlertCircle, ArrowLeft } from "lucide-react";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { useNavigate } from "react-router-dom";
import { getWithoutTokenAPI } from "../util/Api";

const staticSections = [
  {
    title: "1. Introduction",
    content:
      "These Terms & Conditions govern your use of the Aishwarya matrimony platform, including website and mobile application, owned and operated by Aishwarya Matrimony Team. By accessing or using our Services, you agree to be bound by these Terms.",
  },
  {
    title: "2. Eligibility",
    list: [
      "Users must be 18 years or older.",
      "Users confirm they are legally free to marry as per Indian laws.",
      "Profiles must be created only for self or with explicit permission of the profile owner.",
    ],
  },
  {
    title: "3. Account Creation & Verification",
    content:
      "You are responsible for maintaining the confidentiality of your account and login details. The information provided must be true, current, and accurate.",
  },
  {
    title: "4. User Responsibilities",
    content: "You agree NOT to post offensive content, create fake profiles, request money from users, or use the platform for illegal activities.",
  },
  {
    title: "5. Subscription & Payment",
    content:
      "Subscription plans provide additional features. Payments are processed through Razorpay. Once purchased, subscriptions are non-refundable except as required by law.",
  },
  {
    title: "6. Limitation of Liability",
    content:
      "Aishwarya matrimony is not liable for damages arising from misuse of the platform, data loss, or misrepresentation by users.",
  },
  {
    title: "7. Governing Law",
    content:
      "These Terms are governed by the laws of India. Any dispute will be handled exclusively in Courts in India.",
  },
];

const TermsAndConditions = () => {
  const navigate = useNavigate();
  const [termsData, setTermsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const response = await getWithoutTokenAPI("/terms-condition/get_all");
        if (response?.statusCode === 200 && response.data) {
          const data = Array.isArray(response.data)
            ? response.data[0]
            : response.data;
          if (data && !data.is_deleted) {
            setTermsData(data);
          }
        }
      } catch (error) {
        console.error("Error fetching terms:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTerms();
  }, []);

  return (
    <>
      <Header />

      {/* Back link */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate("/")}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-base font-medium">Back</span>
            </button>
          </div>
        </div>
      </div>

      {/* Page Content */}
      <div style={{minHeight: "100vh", paddingTop: "2rem"}}>
        <div
          style={{
            width: "90%",
            maxWidth: "900px",
            margin: "0 auto 4rem auto",
            backgroundColor: "white",
            padding: "3rem",
            borderRadius: "16px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.08)",
          }}
        >
          {/* Header */}
          <div
            style={{
              textAlign: "center",
              marginBottom: "2.5rem",
              paddingBottom: "2rem",
              borderBottom: "2px solid #fee2e2",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "1rem",
              }}
            >
              <div
                style={{
                  padding: "12px",
                  borderRadius: "12px",
                  background: "linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))",
                  color: "white",
                  flexShrink: 0,
                }}
              >
                <FileText size={28} />
              </div>
              <h1
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "700",
                  background: "linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  margin: 0,
                  lineHeight: 1.2,
                }}
              >
                {termsData?.title || "Terms & Conditions"}
              </h1>
            </div>
            <p style={{ fontSize: "0.95rem", color: "#888" }}>
              Last Updated:{" "}
              {new Date().toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>

          {/* Safety Banner */}
          <div
            style={{
              backgroundColor: "#fef3c7",
              borderLeft: "4px solid #f59e0b",
              padding: "1.25rem",
              borderRadius: "8px",
              marginBottom: "2.5rem",
              display: "flex",
              gap: "12px",
              alignItems: "flex-start",
            }}
          >
            <AlertCircle size={20} color="#92400e" style={{ flexShrink: 0, marginTop: "2px" }} />
            <p style={{ fontSize: "0.95rem", color: "#92400e", margin: 0, fontWeight: "500", lineHeight: "1.6" }}>
              For your safety, we strongly recommend meeting in public places, informing family
              members, and avoiding financial transactions with other members.
            </p>
          </div>

          {/* Dynamic Content from API */}
          {loading ? (
            <div style={{ textAlign: "center", padding: "2rem", color: "#9ca3af" }}>Loading...</div>
          ) : termsData?.content ? (
            <div
              style={{
                fontSize: "1rem",
                lineHeight: "1.8",
                color: "#374151",
              }}
            >
              {/* If content has HTML tags render as HTML, otherwise as plain text */}
              {termsData.content.includes("<") ? (
                <div dangerouslySetInnerHTML={{ __html: termsData.content }} />
              ) : (
                <p style={{ whiteSpace: "pre-wrap" }}>{termsData.content}</p>
              )}
            </div>
          ) : (
            /* Fallback static content */
            <div>
              {staticSections.map((section, index) => (
                <div
                  key={index}
                  style={{
                    padding: "1.5rem 0",
                    borderBottom: index === staticSections.length - 1 ? "none" : "1px solid #f3f4f6",
                  }}
                >
                  <h2
                    style={{
                      fontSize: "1.15rem",
                      fontWeight: "600",
                      background: "linear-gradient(to right bottom, rgb(220, 38, 38), rgb(243, 67, 67))",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      marginBottom: "0.75rem",
                    }}
                  >
                    {section.title}
                  </h2>
                  {section.content && (
                    <p style={{ fontSize: "1rem", lineHeight: "1.7", color: "#4b5563", margin: 0 }}>
                      {section.content}
                    </p>
                  )}
                  {section.list && (
                    <ul style={{ paddingLeft: "1.5rem", margin: "0.5rem 0 0" }}>
                      {section.list.map((item, idx) => (
                        <li key={idx} style={{ fontSize: "1rem", lineHeight: "1.7", color: "#4b5563", marginBottom: "0.4rem" }}>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <Footer />
      </div>
    </>
  );
};

export default TermsAndConditions;
